import { useEffect, useRef, useState } from "react";
import { Check, Circle, Clock, MessageSquare, Minus } from "lucide-react";
import { api } from "../api";
import { ErrorState, LoadingState } from "./Ui";
import "../polish/setup-checklist.css";

type Send = (path: string, body?: Record<string, unknown>, method?: string, signal?: AbortSignal) => Promise<any>;
const staffSend: Send = (path, body, method = "GET", signal) => api(path, { method, signal, ...(body ? { body: JSON.stringify(body) } : {}) });

type Item = { id: string; builtin: boolean; lane: "client" | "agency"; required: boolean; title: string; how: string | null;
  state: "done" | "not_needed" | "replied" | "open"; auto: boolean; detail: string | null; note: string | null; answer: string | null; answered_at: string | null };
type Progress = { done: number; total: number; pct: number };
type Data = { items: Item[]; progress: Progress; client: Progress; agency: Progress };

const STATE: Record<Item["state"], { label: string; Icon: typeof Check }> = {
  done: { label: "Done", Icon: Check }, not_needed: { label: "Not needed", Icon: Minus },
  replied: { label: "Reply sent", Icon: MessageSquare }, open: { label: "Open", Icon: Circle },
};

/**
 * The setup checklist for one client: what is still open to run them fully, split into what
 * the owner does and what the agency does. Built-in items tick themselves from NOLA's data.
 * `staff` shows the team's controls (done, not needed, reopen, add an item); owners reply on
 * their own items, which puts a task in the team's Work queue.
 */
export default function SetupChecklist({ clientId, send = staffSend, staff = false }: { clientId: number; send?: Send; staff?: boolean }) {
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const sendRef = useRef(send);
  sendRef.current = send;
  const base = `/api/setup-checklist/${clientId}`;

  useEffect(() => {
    const ctl = new AbortController();
    setError("");
    sendRef.current(base, undefined, "GET", ctl.signal).then((d: Data) => setData(d))
      .catch((e: unknown) => { if (!ctl.signal.aborted) setError(e instanceof Error ? e.message : "The checklist could not load."); });
    return () => ctl.abort();
  }, [base, attempt]);

  async function act(key: string, path: string, body: Record<string, unknown> | undefined, method: string, done: string) {
    setBusy(key); setNotice(null);
    try { setData(await sendRef.current(path, body, method)); setNotice({ ok: true, text: done }); return true; }
    catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : "That did not save." }); return false; }
    finally { setBusy(""); }
  }

  if (error) return <ErrorState message={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!data) return <LoadingState label="Loading the setup checklist" />;

  const lane = (which: "client" | "agency") => data.items.filter((i) => i.lane === which)
    .sort((a, b) => order(a) - order(b) || Number(b.required) - Number(a.required));
  return (
    <div className="setup-checklist">
      <section className="panel setup-progress" aria-label="Setup progress">
        <div><strong>{data.progress.done} of {data.progress.total}</strong> required setup items done</div>
        <div className="setup-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={data.progress.pct}><span style={{ width: `${data.progress.pct}%` }} /></div>
      </section>
      {notice && <p className={`inline-notice ${notice.ok ? "success" : "danger"}`} role={notice.ok ? "status" : "alert"}>{notice.text}</p>}
      <Lane title={staff ? "On the client's side" : "On your side"} note={staff ? "What the owner has to do. They see this list in their portal and can reply on each item." : "These need you. Reply on an item when it is done or if you have a question, and we take it from there."}
        progress={data.client} items={lane("client")} staff={staff} busy={busy} base={base} act={act} />
      <Lane title={staff ? "On our side" : "On our side"} note={staff ? "What the team sets up. Items tick themselves when NOLA sees them working." : "What our team sets up for you."}
        progress={data.agency} items={lane("agency")} staff={staff} busy={busy} base={base} act={act} />
      {staff && <AddItem busy={busy === "add"} onAdd={(body) => act("add", `${base}/items`, body, "POST", "Item added.")} />}
    </div>
  );
}
const order = (i: Item) => ({ open: 0, replied: 1, done: 2, not_needed: 3 })[i.state];
/** Built-in titles that name an internal tool, in the owner's words (the team keeps the tool name). */
const OWNER_TITLE: Record<string, string> = {
  brightlocal: "Link reviews and rank tracking", brand: "Record your brand voice and rules", portal_login: "Set up your portal access",
  ai_search: "Run your first AI search check", seo_plan: "Start your weekly SEO plan", conversions: "Send your booked jobs to Google Ads",
};

type ActFn = (key: string, path: string, body: Record<string, unknown> | undefined, method: string, done: string) => Promise<boolean>;
function Lane({ title, note, progress, items, staff, busy, base, act }: { title: string; note: string; progress: Progress; items: Item[]; staff: boolean; busy: string; base: string; act: ActFn }) {
  return (
    <section className="panel">
      <div className="panel-heading"><div><h2>{title} <small className="muted">{progress.done} of {progress.total}</small></h2><p className="setup-note">{note}</p></div></div>
      {items.length ? <ul className="setup-items">{items.map((i) => <Row key={i.id} item={i} staff={staff} busy={busy} base={base} act={act} />)}</ul>
        : <p className="setup-empty">Nothing on this side.</p>}
    </section>
  );
}

function Row({ item, staff, busy, base, act }: { item: Item; staff: boolean; busy: string; base: string; act: ActFn }) {
  const [reply, setReply] = useState("");
  const [open, setOpen] = useState(false);
  const { label, Icon } = STATE[item.state];
  const path = `${base}/items/${item.id}`;
  const canReply = item.lane === "client" && item.state !== "done" && item.state !== "not_needed";
  // The owner reads the agency's open items as work in progress, not as boxes of their own to tick.
  const theirs = !staff && item.lane === "agency";
  const Mark = theirs && item.state === "open" ? Clock : Icon;
  const markLabel = theirs && item.state === "open" ? "In progress" : label;
  return (
    <li className={`setup-item is-${item.state}${theirs ? " is-theirs" : ""}`}>
      <span className="setup-mark" title={markLabel}><Mark aria-hidden="true" /><span className="sr-only">{markLabel}</span></span>
      <div className="setup-body">
        <div className="setup-title"><strong>{(!staff && item.builtin && OWNER_TITLE[item.id]) || item.title}</strong>{!item.required && <small className="setup-soft">Optional</small>}
          {item.state === "replied" && <span className="setup-tag">Reply sent</span>}
          {item.state === "done" && item.auto && <small className="setup-soft">Checked automatically</small>}</div>
        {item.detail && <p className="setup-detail">{item.detail}</p>}
        {item.how && item.state !== "done" && item.state !== "not_needed" && <p className="setup-how">{item.how}</p>}
        {item.answer && <p className="setup-answer"><span className="muted">Reply:</span> {item.answer}</p>}
        {item.note && staff && <p className="setup-detail">Team note: {item.note}</p>}
        <div className="setup-actions">
          {canReply && !open && <button type="button" className="text-button" onClick={() => setOpen(true)}>{item.answer ? "Send another reply" : "Reply"}</button>}
          {staff && item.state !== "done" && !item.auto && <button type="button" className="text-button" disabled={!!busy} onClick={() => void act(item.id, path, { status: "done" }, "PUT", "Marked done.")}>Mark done</button>}
          {staff && item.state !== "not_needed" && !item.auto && <button type="button" className="text-button" disabled={!!busy} onClick={() => void act(item.id, path, { status: "not_needed" }, "PUT", "Marked not needed.")}>Not needed</button>}
          {staff && !item.auto && (item.state === "done" || item.state === "not_needed") && <button type="button" className="text-button" disabled={!!busy} onClick={() => void act(item.id, path, { status: "open" }, "PUT", "Opened again.")}>Open again</button>}
          {staff && !item.builtin && <button type="button" className="text-button" disabled={!!busy} onClick={() => void act(item.id, path, undefined, "DELETE", "Item removed.")}>Remove</button>}
        </div>
        {open && (
          <form className="setup-reply" onSubmit={async (e) => { e.preventDefault(); if (await act(item.id, `${path}/answer`, { answer: reply }, "POST", "Reply sent. The team will check it.")) { setReply(""); setOpen(false); } }}>
            <label className="sr-only" htmlFor={`reply-${item.id}`}>Your reply</label>
            <textarea id={`reply-${item.id}`} rows={3} maxLength={2000} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Done, or what you need from us" />
            <div className="setup-reply-actions">
              <button type="submit" className="button primary" disabled={busy === item.id || !reply.trim()}>{busy === item.id ? "Sending…" : "Send reply"}</button>
              <button type="button" className="button secondary" onClick={() => { setOpen(false); setReply(""); }}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </li>
  );
}

function AddItem({ busy, onAdd }: { busy: boolean; onAdd: (body: Record<string, unknown>) => Promise<boolean> }) {
  const [title, setTitle] = useState(""), [lane, setLane] = useState<"client" | "agency">("client"), [how, setHow] = useState(""), [required, setRequired] = useState(true);
  return (
    <section className="panel">
      <div className="panel-heading"><div><h2>Add an item</h2></div></div>
      <form className="setup-add" onSubmit={async (e) => { e.preventDefault(); if (await onAdd({ title, lane, how: how || undefined, required })) { setTitle(""); setHow(""); } }}>
        <label><span className="field-label">What needs doing</span><input id="setup-add-title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} /></label>
        <label><span className="field-label">Who does it</span><select id="setup-add-lane" value={lane} onChange={(e) => setLane(e.target.value as "client" | "agency")}><option value="client">The client</option><option value="agency">Our team</option></select></label>
        <label className="setup-add-wide"><span className="field-label">Steps for the client (optional)</span><input id="setup-add-how" value={how} maxLength={1000} onChange={(e) => setHow(e.target.value)} /></label>
        <label className="setup-add-check"><input id="setup-add-required" type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />Required</label>
        <div><button type="submit" className="button secondary" disabled={busy || title.trim().length < 2}>{busy ? "Adding…" : "Add item"}</button></div>
      </form>
    </section>
  );
}
